import {
  type ReactNode,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Box,
  CheckIcon,
  CloseIcon,
  ChakraVisuallyHidden,
  DictationMicrophoneIcon,
  DictationMicrophoneOffIcon,
  IconButton,
  MotionBox,
  motionDurations,
  motionEasings,
  Row,
  Spinner,
  Text,
  Tooltip,
} from "@hightouch/htui";
import { useReducedMotion } from "motion/react";
import type { ComposerDictation } from "src/components/llm/hooks/use-composer-dictation";

const WAVEFORM_BAR_SPACING = 5;
const WAVEFORM_SCROLL_SPEED = 48;
const WAVEFORM_GROW_DURATION = 220;
const WAVEFORM_EDGE_FADE = 24;

type Props = {
  dictation: ComposerDictation;
};

type DictationToolbarProps = Props & {
  children: ReactNode;
  textareaRef: RefObject<HTMLTextAreaElement>;
};

export function DictationControl({ dictation }: Props) {
  if (!dictation.enabled) return null;
  const message =
    dictation.error?.message ??
    (!dictation.supported
      ? "Dictation is unavailable in this browser. Try another browser."
      : "Start dictation");
  return (
    <Tooltip message={message}>
      <IconButton
        type="button"
        size="md"
        variant="tertiary"
        shape="circle"
        aria-label={message}
        icon={
          dictation.error?.blocked
            ? DictationMicrophoneOffIcon
            : DictationMicrophoneIcon
        }
        color={
          dictation.error?.blocked || !dictation.supported
            ? "text.disabled"
            : undefined
        }
        isDisabled={dictation.disabled}
        aria-disabled={
          dictation.disabled || !dictation.supported || dictation.busy
        }
        onClick={(event) => {
          event.stopPropagation();
          if (!dictation.busy) void dictation.start();
        }}
      />
    </Tooltip>
  );
}

export function DictationToolbar({
  dictation,
  children,
  textareaRef,
}: DictationToolbarProps) {
  const toolbarRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const wasBusy = useRef(false);
  const reduceMotion = useReducedMotion();
  const { busy, state, cancel, stop, error } = dictation;
  const cancelLabel =
    state === "transcribing" ? "Cancel transcription" : "Cancel dictation";
  const label =
    state === "recording"
      ? "Recording…"
      : state === "requesting"
        ? "Waiting for microphone…"
        : "Transcribing…";

  useEffect(() => {
    if (busy && !wasBusy.current)
      cancelRef.current?.focus({ preventScroll: true });
    if (
      !busy &&
      wasBusy.current &&
      (document.activeElement === document.body ||
        toolbarRef.current?.contains(document.activeElement))
    ) {
      textareaRef.current?.focus({ preventScroll: true });
    }
    wasBusy.current = busy;
  }, [busy, textareaRef]);

  useEffect(() => {
    if (!busy) return;
    const ownerDocument = toolbarRef.current?.ownerDocument;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.isComposing) return;
      if (
        event.target !== textareaRef.current &&
        !(
          event.target instanceof Node &&
          toolbarRef.current?.contains(event.target)
        )
      )
        return;
      // Handle the draft and toolbar before an enclosing dialog dismisses.
      event.preventDefault();
      event.stopPropagation();
      cancel();
    };
    ownerDocument?.addEventListener("keydown", onKeyDown, true);
    return () => ownerDocument?.removeEventListener("keydown", onKeyDown, true);
  }, [busy, cancel, textareaRef]);

  return (
    <Box
      ref={toolbarRef}
      role={busy ? "group" : undefined}
      aria-label={busy ? "Dictation controls" : undefined}
      position="relative"
      width="100%"
      minWidth={0}
      onClick={busy ? (event) => event.stopPropagation() : undefined}
    >
      <MotionBox
        initial={false}
        animate={{ opacity: busy ? 0 : 1 }}
        transition={{
          duration: reduceMotion ? 0 : motionDurations.fast,
          ease: motionEasings.easeOutCubic,
        }}
        visibility={busy ? "hidden" : "visible"}
        aria-hidden={busy || undefined}
      >
        {children}
      </MotionBox>
      {busy && (
        <MotionBox
          position="absolute"
          inset={0}
          display="flex"
          alignItems="center"
          initial={reduceMotion ? false : { opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: reduceMotion ? 0 : motionDurations.normal,
            ease: motionEasings.easeOutCubic,
          }}
        >
          <Row width="100%" align="center" gap={3}>
            <Tooltip message={cancelLabel}>
              <IconButton
                ref={cancelRef}
                type="button"
                size="md"
                variant="secondary"
                shape="circle"
                position="relative"
                _before={{
                  content: '""',
                  position: "absolute",
                  inset: -1.5,
                  borderRadius: "full",
                }}
                aria-label={cancelLabel}
                icon={CloseIcon}
                onClick={cancel}
              />
            </Tooltip>
            <MotionBox
              key={state}
              flex={1}
              minWidth={0}
              aria-hidden="true"
              initial={reduceMotion ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{
                duration: reduceMotion ? 0 : motionDurations.fast,
                ease: motionEasings.easeOutCubic,
              }}
            >
              {state === "recording" ? (
                <DictationRecording dictation={dictation} />
              ) : (
                <Row align="center" justify="center" gap={2}>
                  {state === "requesting" && !reduceMotion && (
                    <Spinner size="xs" color="currentColor" />
                  )}
                  <Text
                    size="sm"
                    color="text.secondary"
                    shimmer={state === "transcribing" && !reduceMotion}
                    shimmerColor="base.border"
                  >
                    {label}
                  </Text>
                </Row>
              )}
            </MotionBox>
            <Tooltip
              message="Finish dictation"
              isDisabled={state !== "recording"}
              keepMountedWhileDisabled
            >
              <IconButton
                type="button"
                size="md"
                variant="primary"
                shape="circle"
                position="relative"
                _before={{
                  content: '""',
                  position: "absolute",
                  inset: -1.5,
                  borderRadius: "full",
                }}
                aria-label="Finish dictation"
                icon={CheckIcon}
                bg="text.primary"
                color="white"
                _hover={{ bg: "text.secondary" }}
                _active={{ bg: "text.primary" }}
                aria-disabled={state !== "recording"}
                onClick={() => {
                  if (state === "recording") stop();
                }}
              />
            </Tooltip>
          </Row>
        </MotionBox>
      )}
      <ChakraVisuallyHidden role="status" aria-atomic="true">
        {error?.message ?? (busy ? label : "")}
      </ChakraVisuallyHidden>
    </Box>
  );
}

function DictationRecording({ dictation }: Props) {
  const [seconds, setSeconds] = useState(0);
  const { startedAt, analyser } = dictation;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    if (startedAt === undefined) return;
    const update = () =>
      setSeconds(Math.floor((Date.now() - startedAt) / 1000));
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [startedAt]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || reduceMotion) return;
    const context = canvas.getContext("2d");
    if (!context) return;
    const values = analyser
      ? new Uint8Array(analyser.frequencyBinCount)
      : undefined;
    let bars: { x: number; value: number; bornAt: number }[] = [];
    let width = 0;
    let height = 0;
    let frame = 0;
    let lastFrame = performance.now();
    let level = 0;
    let peak = 0;
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const ratio = window.devicePixelRatio || 1;
      width = rect.width;
      height = rect.height;
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.fillStyle = getComputedStyle(canvas).color;
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    const draw = (time: number) => {
      const elapsed = Math.min(100, time - lastFrame);
      lastFrame = time;
      if (analyser && values) {
        analyser.getByteTimeDomainData(values);
        const rms = Math.sqrt(
          values.reduce((sum, value) => sum + ((value - 128) / 128) ** 2, 0) /
            values.length,
        );
        // Soft saturation keeps loud syllables distinct instead of clipping their peaks.
        const target = 1 - Math.exp(-rms * 10);
        const responseTime = target > level ? 20 : 70;
        level += (target - level) * (1 - Math.exp(-elapsed / responseTime));
      }
      // Keep brief syllables that land between bar samples (adapted from #50602).
      peak = Math.max(peak, level);
      for (const bar of bars) bar.x -= (WAVEFORM_SCROLL_SPEED * elapsed) / 1000;
      bars = bars.filter((bar) => bar.x > -WAVEFORM_BAR_SPACING);
      let first = bars[0];
      while (!first || first.x > 0) {
        first = {
          x: first ? first.x - WAVEFORM_BAR_SPACING : width,
          value: 0,
          bornAt: time - WAVEFORM_GROW_DURATION,
        };
        bars.unshift(first);
      }
      let last = bars[bars.length - 1];
      while (!last || last.x < width) {
        last = {
          x: last ? last.x + WAVEFORM_BAR_SPACING : width,
          value: peak,
          bornAt: time,
        };
        bars.push(last);
        peak = level;
      }
      context.clearRect(0, 0, width, height);
      for (const { x, value, bornAt } of bars) {
        if (x < 0 || x > width) continue;
        const progress = Math.min(1, (time - bornAt) / WAVEFORM_GROW_DURATION);
        const grown = 1 - (1 - progress) ** 3;
        const barHeight = 2 + Math.max(0, value * (height - 4) - 2) * grown;
        const edge = Math.max(
          0,
          Math.min(1, x / WAVEFORM_EDGE_FADE, (width - x) / WAVEFORM_EDGE_FADE),
        );
        const fade = edge * edge * (3 - 2 * edge);
        context.globalAlpha = fade * (0.25 + 0.75 * (x / width));
        context.beginPath();
        context.roundRect(x, (height - barHeight) / 2, 2, barHeight, 1);
        context.fill();
      }
      if (analyser) frame = requestAnimationFrame(draw);
    };
    draw(lastFrame);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [analyser, reduceMotion]);

  return (
    <Row align="center" gap={3}>
      {reduceMotion || !analyser ? (
        <Box flex={1}>
          <Text size="sm" color="text.secondary">
            Recording…
          </Text>
        </Box>
      ) : (
        <Box
          as="canvas"
          ref={canvasRef}
          flex={1}
          width="100%"
          height={8}
          minWidth={0}
          color="text.primary"
        />
      )}
      <Box
        flexShrink={0}
        sx={{ fontVariantNumeric: "tabular-nums" }}
        minWidth={8}
        textAlign="right"
      >
        <Text size="sm" color="text.secondary">
          {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")}
        </Text>
      </Box>
    </Row>
  );
}
